import React, {
    MouseEvent,
    ReactNode,
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
    useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';

export interface PopoverProps {
    children: ReactNode;
    content: ReactNode;
    trigger?: 'click' | 'hover';
    preferredPosition?:
        | 'top'
        | 'bottom'
        | 'left'
        | 'right'
        | 'top-left'
        | 'top-right'
        | 'bottom-left'
        | 'bottom-right';
    offset?: number;
    className?: string;
    contentClassName?: string;
    disabled?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
}

export interface PopoverHandle {
    open: () => void;
    close: () => void;
}

interface Position {
    top: number;
    left: number;
    transform?: string;
}

const Popover = forwardRef<PopoverHandle, PopoverProps>((props, ref) => {
    const {
        children,
        content,
        trigger = 'click',
        preferredPosition = 'bottom',
        offset = 8,
        className = '',
        contentClassName = '',
        disabled = false,
        onOpen,
        onClose,
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [isPositioned, setIsPositioned] = useState(false);
    const [position, setPosition] = useState<Position>({ top: 0, left: 0 });

    const triggerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const rafId = useRef<number | null>(null);

    const calculatePosition = (): Position => {
        if (!triggerRef.current || !contentRef.current) return { top: 0, left: 0 };

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const contentRect = contentRef.current.getBoundingClientRect();
        const viewport = { width: window.innerWidth, height: window.innerHeight };

        const positions = {
            top: {
                top: triggerRect.top - contentRect.height - offset,
                left: triggerRect.left + triggerRect.width / 2,
                transform: 'translateX(-50%)',
            },
            bottom: {
                top: triggerRect.bottom + offset,
                left: triggerRect.left + triggerRect.width / 2,
                transform: 'translateX(-50%)',
            },
            left: {
                top: triggerRect.top + triggerRect.height / 2,
                left: triggerRect.left - contentRect.width - offset,
                transform: 'translateY(-50%)',
            },
            right: {
                top: triggerRect.top + triggerRect.height / 2,
                left: triggerRect.right + offset,
                transform: 'translateY(-50%)',
            },
            'top-left': {
                top: triggerRect.top - contentRect.height - offset,
                left: triggerRect.left,
            },
            'top-right': {
                top: triggerRect.top - contentRect.height - offset,
                left: triggerRect.right - contentRect.width,
            },
            'bottom-left': {
                top: triggerRect.bottom + offset,
                left: triggerRect.left,
            },
            'bottom-right': {
                top: triggerRect.bottom + offset,
                left: triggerRect.right - contentRect.width,
            },
        } satisfies Record<string, Position>;

        const checkPosition = (pos: Position): boolean => {
            let adjustedTop = pos.top;
            let adjustedLeft = pos.left;

            if (pos.transform?.includes('translateX(-50%)')) {
                adjustedLeft = pos.left - contentRect.width / 2;
            }
            if (pos.transform?.includes('translateY(-50%)')) {
                adjustedTop = pos.top - contentRect.height / 2;
            }

            return (
                adjustedTop >= 0 &&
                adjustedTop + contentRect.height <= viewport.height &&
                adjustedLeft >= 0 &&
                adjustedLeft + contentRect.width <= viewport.width
            );
        };

        const preferredPos = positions[preferredPosition];
        if (checkPosition(preferredPos)) return preferredPos;

        const fallbackOrder: (keyof typeof positions)[] = [
            'bottom',
            'top',
            'right',
            'left',
            'bottom-left',
            'bottom-right',
            'top-left',
            'top-right',
        ];

        for (const name of fallbackOrder) {
            if (name === preferredPosition) continue;
            const pos = positions[name];
            if (checkPosition(pos)) return pos;
        }

        // korekta “na siłę” jeśli nic nie pasuje
        const corrected = { ...preferredPos };

        if (corrected.left < 0) corrected.left = 8;
        else if (corrected.left + contentRect.width > viewport.width) {
            corrected.left = viewport.width - contentRect.width - 8;
        }

        if (corrected.top < 0) corrected.top = 8;
        else if (corrected.top + contentRect.height > viewport.height) {
            corrected.top = viewport.height - contentRect.height - 8;
        }

        return corrected;
    };

    const scheduleUpdatePosition = () => {
        if (!isOpen) return;

        if (rafId.current != null) cancelAnimationFrame(rafId.current);
        rafId.current = requestAnimationFrame(() => {
            // Jeśli jeszcze nie ma refów (np. w trakcie montowania), nie “odkrywaj” popovera
            if (!triggerRef.current || !contentRef.current) {
                setIsPositioned(false);
                return;
            }
            const newPos = calculatePosition();
            setPosition(newPos);
            setIsPositioned(true);
        });
    };

    const handleOpen = (e?: MouseEvent) => {
        e?.stopPropagation();
        if (disabled) return;
        setIsPositioned(false);
        setIsOpen(true);
        onOpen?.();
    };

    const handleClose = (e?: MouseEvent) => {
        e?.stopPropagation();
        setIsOpen(false);
        setIsPositioned(false);
        onClose?.();
    };

    const handleTriggerClick = (e?: MouseEvent) => {
        if (trigger === 'click') {
            isOpen ? handleClose(e) : handleOpen(e);
        }
    };

    const handleTriggerMouseEnter = () => {
        if (trigger === 'hover') handleOpen();
    };

    const handleTriggerMouseLeave = () => {
        if (trigger === 'hover') handleClose();
    };

    useImperativeHandle(ref, () => ({
        open: () => handleOpen(),
        close: () => handleClose(),
    }));

    // Kluczowe: policz pozycję zanim przeglądarka narysuje (brak “skoku”)
    useLayoutEffect(() => {
        if (!isOpen) return;
        scheduleUpdatePosition();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Aktualizacje “przyklejenia”: scroll (także w kontenerach), resize, zmiana rozmiaru triggera/contetu
    useEffect(() => {
        if (!isOpen) return;

        const onScroll = () => scheduleUpdatePosition();
        const onResize = () => scheduleUpdatePosition();

        // capture=true -> łapie scroll na dowolnym scrollowalnym rodzicu
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);

        const ro = new ResizeObserver(() => scheduleUpdatePosition());
        if (triggerRef.current) ro.observe(triggerRef.current);
        if (contentRef.current) ro.observe(contentRef.current);

        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
            ro.disconnect();
            if (rafId.current != null) cancelAnimationFrame(rafId.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Zamknięcie po kliknięciu poza
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: Event) => {
            if (
                triggerRef.current &&
                !triggerRef.current.contains(event.target as Node) &&
                contentRef.current &&
                !contentRef.current.contains(event.target as Node)
            ) {
                handleClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    return (
        <>
            <div
                ref={triggerRef}
                className={`inline-block ${className}`}
                onClick={(e) => handleTriggerClick(e)}
                onMouseEnter={handleTriggerMouseEnter}
                onMouseLeave={handleTriggerMouseLeave}
            >
                {children}
            </div>

            {isOpen &&
                createPortal(
                    <div
                        ref={contentRef}
                        className={`fixed z-50 ${contentClassName}`}
                        style={{
                            top: position.top,
                            left: position.left,
                            transform: position.transform,
                            visibility: isPositioned ? 'visible' : 'hidden', // brak “mignięcia”
                        }}
                    >
                        {content}
                    </div>,
                    document.body
                )}
        </>
    );
});

Popover.displayName = 'Popover';

export default Popover;
